/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import ReactDOM from 'react-dom';
import { IconButton, Box, useMediaQuery, useTheme, Snackbar } from '@mui/material';
import ReactPlayer from 'react-player/youtube';
import { usePlayback } from 'contexts/WatchContext';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Persistent single YouTube player rendered into the active placeholder.
 */
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import { videoPath } from 'routes';

interface PersistentYtPlayerProps {
  /** Placeholder refs from Layout to measure video page position */
  desktopPlaceholderRef: React.RefObject<HTMLDivElement>;
  mobilePlaceholderRef: React.RefObject<HTMLDivElement>;
  /** Placeholder ref for mini player on non-video pages */
  miniPlaceholderRef: React.RefObject<HTMLDivElement>;
  /** Whether currently on the video page */
  isVideoPage: boolean;
}
const PersistentYtPlayer: React.FC<PersistentYtPlayerProps> = ({
  desktopPlaceholderRef,
  mobilePlaceholderRef,
  miniPlaceholderRef,
  isVideoPage
}) => {
  const playback = usePlayback();
  // Re-measure player position when drawer opens/closes
  const { drawerOpen } = playback;
  const navigate = useNavigate();
  const location = useLocation();
  const current = playback.currentVideo;
  // Error skip handling: limit sequential skips and suppress multiple skips per ID
  const MAX_SKIP = 3;
  const skipErrorCount = React.useRef(0);
  // Track which video IDs have already been skipped to avoid double-skipping
  const erroredIds = React.useRef<Set<string>>(new Set());
  // Suppress duplicate onError events until playback resumes
  const skippingRef = React.useRef(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMsg, setSnackbarMsg] = React.useState('');
  // Reset skip counter and clear skip marker for this video on load
  React.useEffect(() => {
    skipErrorCount.current = 0;
    if (current) {
      erroredIds.current.delete(current.musicVideo);
    }
  }, [current]);
  // Persistent player ref
  const playerRef = React.useRef<any>(null);
  // Embed URL using privacy-enhanced domain; playerVars below control branding and related videos
  const embedUrl = current
    ? `https://www.youtube-nocookie.com/embed/${current.musicVideo}`
    : undefined;
  // Loading flag to show thumbnail overlay during cueing
  const [loading, setLoading] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Determine intended placeholder (video page vs mini player)
  const intendedRef = isVideoPage
    ? (isMobile ? mobilePlaceholderRef : desktopPlaceholderRef)
    : miniPlaceholderRef;
  // Track geometry of the placeholder for positioning; only update when it changes
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  // Horizontal position of mini-player (for dragging), initialized from rect.left
  const [posLeft, setPosLeft] = React.useState<number | null>(null);
  // Dragging state
  const [dragging, setDragging] = React.useState(false);
  const dragStartX = React.useRef(0);
  const dragStartLeft = React.useRef(0);
  // Measure placeholder position and update if geometry differs
  const measure = React.useCallback(() => {
    const el = intendedRef.current;
    if (el) {
      const newRect = el.getBoundingClientRect();
      setRect(prev => {
        if (
          !prev ||
          newRect.top !== prev.top ||
          newRect.left !== prev.left ||
          newRect.width !== prev.width ||
          newRect.height !== prev.height
        ) {
          return newRect;
        }
        return prev;
      });
    }
  }, [intendedRef]);
  // Subscribe to placeholder size and position changes; perform initial measurement
  React.useEffect(() => {
    let rafId: number;
    // ResizeObserver only tracks element size, not position
    const ro = new ResizeObserver(measure);
    // Poll until placeholder is available, then observe
    const attachObserver = () => {
      const node = intendedRef.current;
      if (node) {
        measure();
        ro.observe(node);
      } else {
        rafId = requestAnimationFrame(attachObserver);
      }
    };
    attachObserver();
    // Also re-measure on window scroll/resize/touch to account for layout changes
    let scheduled = false;
    const scheduleMeasure = () => {
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          measure();
        });
      }
    };
    window.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('resize', scheduleMeasure);
    window.addEventListener('touchmove', scheduleMeasure, { passive: true });
    // Cleanup
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('touchmove', scheduleMeasure);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [measure]);
  // Re-measure when placeholder position changes (e.g., nav strip height affects top offset)
  React.useEffect(() => {
    const node = intendedRef.current;
    if (!node) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'style') {
          measure();
          break;
        }
      }
    });
    mo.observe(node, { attributes: true, attributeFilter: ['style'] });
    return () => mo.disconnect();
  }, [measure]);
  // Re-measure when drawer toggles to accommodate CSS margin transition
  React.useEffect(() => {
    measure();
    const timeoutId = window.setTimeout(measure, theme.transitions.duration.enteringScreen);
    return () => window.clearTimeout(timeoutId);
  }, [drawerOpen, measure, theme.transitions.duration.enteringScreen]);
  // Imperatively cue and play new video without remount to avoid white-flash
  React.useEffect(() => {
    if (current && playerRef.current) {
      const internal = playerRef.current.getInternalPlayer();
      if (internal && typeof internal.cueVideoById === 'function') {
        setLoading(true);
        // Cue the new video at the beginning (startSeconds = 0)
        internal.cueVideoById(current.musicVideo, 0);
        // Trigger playback once cued
        if (typeof internal.playVideo === 'function') {
          internal.playVideo();
        }
      }
    }
  }, [current]);
  // Mini-player drag & position hooks
  // Reset mini-player position on page change
  React.useEffect(() => {
    if (!isVideoPage) {
      setPosLeft(null);
    }
  }, [location.pathname, isVideoPage]);
  // Initialize horizontal position once from placeholder
  React.useEffect(() => {
    if (!isVideoPage && rect) {
      setPosLeft(prev => prev === null ? rect.left : prev);
    }
  }, [rect, isVideoPage]);
  // Recompute position on window resize, keep within bounds
  React.useEffect(() => {
    const handleResize = () => {
      if (!isVideoPage && posLeft !== null && rect) {
        const maxLeft = window.innerWidth - rect.width;
        setPosLeft(prev => prev !== null ? Math.max(0, Math.min(prev, maxLeft)) : prev);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rect, posLeft, isVideoPage]);
  // Setup dragging handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    dragStartX.current = clientX;
    dragStartLeft.current = posLeft !== null ? posLeft : (rect?.left ?? 0);
    setDragging(true);
  };
  React.useEffect(() => {
    if (!dragging) return;
    const handleMove = (ev: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in ev ? (ev as TouchEvent).touches[0].clientX : (ev as MouseEvent).clientX;
      let newLeft = dragStartLeft.current + (clientX - dragStartX.current);
      if (rect) {
        const maxLeft = window.innerWidth - rect.width;
        newLeft = Math.max(0, Math.min(newLeft, maxLeft));
      }
      setPosLeft(newLeft);
    };
    const handleEnd = () => setDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [dragging, rect]);
  const baseStyle = {
    bgcolor: 'black',
    // Use appBar zIndex on video pages; otherwise place below drawer
    zIndex: isVideoPage ? theme.zIndex.appBar : theme.zIndex.drawer - 1,
  };
  // Only render when there is a current video; keep player mounted even if rect not measured yet
  if (!current) {
    return null;
  }
  // Portal the player into the persistent root container
  if (typeof document === 'undefined') {
    return null;
  }
  // Only show once we have a valid measurement rectangle
  const showPlayer = rect !== null;
  // Position player: after first measurement always render at latest rect
  const measuredStyle = showPlayer
    ? (() => {
        // Compute left: allow dragging on mini-player (non-video pages)
        const left = isVideoPage
          ? rect!.left
          : (posLeft !== null ? posLeft : rect!.left);
        return {
          position: 'fixed' as const,
          top: rect!.top,
          left,
          width: rect!.width,
          height: rect!.height,
          pointerEvents: 'auto' as const,
          visibility: 'visible' as const,
        };
      })()
    : {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none' as const,
        visibility: 'hidden' as const,
      };
  return ReactDOM.createPortal(
      <>
        <Box sx={{ ...baseStyle, ...measuredStyle }}>
        <ReactPlayer
          ref={playerRef}
          // URL of the current video
          url={embedUrl}
        config={{
          playerVars: {
            enablejsapi: 1,
            origin: window.location.origin,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
          },
        }}
        playing={playback.isPlaying}
        controls={isVideoPage}
        playsinline
        width="100%"
        height="100%"
        style={{ position: 'absolute', top: 0, left: 0 }}
        onReady={() => {
          // Initial ready: seek to last known time
          const t = playback.playbackTimeRef.current;
          if (t > 0 && playerRef.current) {
            playerRef.current.seekTo(t, 'seconds');
          }
          playback.resume();
          // Reset skip counter on successful load
          skipErrorCount.current = 0;
        }}
        onProgress={({ playedSeconds }) => playback.seek(playedSeconds)}
        onPlay={() => { skippingRef.current = false; setLoading(false); playback.resume(); }}
        onPause={() => playback.pause()}
        onEnded={() => {
          // Advance to the next video: navigate if on the video page, otherwise play inline
          if (isVideoPage) {
            playback.navigateNext();
          } else {
            playback.playNext();
          }
        }}
        onError={(e) => {
          // Ignore duplicate error events until next playback
          if (skippingRef.current) return;
          skippingRef.current = true;
          console.error('Playback error', e);
          // Identify actual errored video ID via internal player data
          let errorVid: string | undefined;
          try {
            const internal = playerRef.current?.getInternalPlayer();
            const info = internal?.getVideoData();
            errorVid = info?.video_id;
          } catch {}
          if (!errorVid && current) errorVid = current.musicVideo;
          if (!errorVid) return;
          // Only skip once per errored video ID
          if (erroredIds.current.has(errorVid)) return;
          erroredIds.current.add(errorVid);
          // Exceeded max distinct skips?
          if (skipErrorCount.current >= MAX_SKIP) {
            setSnackbarMsg("Sorry—these videos aren't available right now.");
            setSnackbarOpen(true);
            return;
          }
          skipErrorCount.current += 1;
          setSnackbarMsg('Video unavailable, skipping…');
          setSnackbarOpen(true);
          // Advance to the next video: navigate if on video page, otherwise play inline
          if (isVideoPage) {
            playback.navigateNext(true);
          } else {
            playback.playNext();
          }
        }}
      />
      {/* Thumbnail overlay while cueing new video to mask white flash */}
      {loading && current && (
        <Box
          component="img"
          src={`https://i.ytimg.com/vi/${current.musicVideo}/hqdefault.jpg`}
          alt="Loading thumbnail"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1,
          }}
        />
      )}
        </Box>
        {!isVideoPage && showPlayer && (
          <>
            {/* Top chrome strip: navigation to video page and close button */}
            <Box
          sx={(theme) => ({
                position: 'fixed',
                top: rect!.top,
                left: isVideoPage ? rect!.left : (posLeft !== null ? posLeft : rect!.left),
                width: rect!.width,
                transform: 'translateY(-100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: theme.spacing(1),
                bgcolor: theme.palette.secondary.main,
                pointerEvents: 'auto',
                // Match player viewport zIndex (below drawer)
                zIndex: theme.zIndex.drawer - 1,
                // Draggable cursor
                cursor: dragging ? 'grabbing' : 'grab',
                // Round top corners of the chrome strip
                borderTopLeftRadius: theme.shape.borderRadius,
              borderTopRightRadius: theme.shape.borderRadius,
              })}
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
            >
            <IconButton
              onClick={() => {
                playback.resume();
                navigate(videoPath(current.musicVideo), {
                  state: {
                    facadeQueue: playback.facadeQueue,
                    shadowQueue: playback.shadowQueue,
                  },
                });
              }}
              sx={(theme) => ({
                color: theme.palette.secondary.contrastText,
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              })}
            >
              <OpenInNewIcon />
            </IconButton>
            <IconButton
              onClick={() => playback.stop()}
              sx={(theme) => ({
                color: theme.palette.secondary.contrastText,
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              })}
            >
              <CloseIcon />
            </IconButton>
            </Box>
            {/* Bottom chrome strip: playback controls */}
            <Box
              sx={(theme) => ({
                position: 'fixed',
                top: rect!.top + rect!.height,
                left: isVideoPage ? rect!.left : (posLeft !== null ? posLeft : rect!.left),
                width: rect!.width,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: theme.spacing(1),
                // Darker grey opaque background for contrast
                bgcolor: theme.palette.grey[200],
                pointerEvents: 'auto',
                // Match player viewport zIndex (below drawer)
                zIndex: theme.zIndex.drawer - 1,
              })}
            >
              <IconButton
                onClick={() => playback.playPrev()}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(128,128,128,0.1)' },
                }}
              >
                <SkipPreviousIcon />
              </IconButton>
              <IconButton
                onClick={() => playback.toggle()}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(128,128,128,0.1)' },
                }}
              >
                {playback.isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <IconButton
                onClick={() => playback.playNext()}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(128,128,128,0.1)' },
                }}
              >
                <SkipNextIcon />
              </IconButton>
            </Box>
          </>
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMsg}
        />
      </>,
      document.body
  );
};
export default PersistentYtPlayer;